import * as React from 'react';
import { Tooltip } from '../../display';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Sidebar context for sharing state between compound components
 */
interface SidebarContextValue {
  collapsed: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

/**
 * Hook to access Sidebar context
 */
function useSidebarContext() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('Sidebar components must be used within a Sidebar component');
  }
  return context;
}

/**
 * Props for the main Sidebar component
 */
export interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Whether the sidebar is collapsed
   * @default false
   */
  collapsed?: boolean;
  /**
   * Callback when the collapsed state changes
   */
  onCollapsedChange?: (collapsed: boolean) => void;
  /**
   * Sidebar content (Header, Section, Footer components)
   */
  children: React.ReactNode;
}

/**
 * Props for the SidebarHeader component
 */
export interface SidebarHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Logo element to display
   */
  logo?: React.ReactNode;
  /**
   * Title text (hidden when collapsed)
   */
  title?: string;
}

/**
 * Props for the SidebarSection component
 */
export interface SidebarSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Section label (hidden when collapsed)
   */
  title?: string;
  /**
   * Section content (SidebarItem components)
   */
  children?: React.ReactNode;
}

/**
 * Props for the SidebarItem component
 */
export interface SidebarItemProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  /**
   * Icon element to display
   */
  icon?: React.ReactNode;
  /**
   * Item label text
   */
  label: string;
  /**
   * Navigation href (renders as <a> if provided)
   */
  href?: string;
  /**
   * Whether this item is currently active
   * @default false
   */
  active?: boolean;
  /**
   * Whether this item is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Nested items (for expandable sub-navigation)
   */
  children?: React.ReactNode;
}

/**
 * Props for the SidebarFooter component
 */
export interface SidebarFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Footer content
   */
  children: React.ReactNode;
}

/**
 * Main Sidebar component
 *
 * A navigation sidebar with expandable/collapsible states.
 * Follows the compound component pattern for flexible composition.
 *
 * @example
 * <Sidebar collapsed={isCollapsed} onCollapsedChange={setIsCollapsed}>
 *   <Sidebar.Header logo={<Logo />} title="Insurance Portal" />
 *   <Sidebar.Section title="Main">
 *     <Sidebar.Item icon={<HomeIcon />} label="Dashboard" href="/dashboard" active />
 *     <Sidebar.Item icon={<PoliciesIcon />} label="Policies" href="/policies" />
 *   </Sidebar.Section>
 *   <Sidebar.Footer>
 *     <Sidebar.Item icon={<SettingsIcon />} label="Settings" href="/settings" />
 *   </Sidebar.Footer>
 * </Sidebar>
 */
const SidebarRoot = React.forwardRef<HTMLElement, SidebarProps>(
  ({ collapsed = false, onCollapsedChange, children, className = '', ...props }, ref) => {
    const contextValue: SidebarContextValue = React.useMemo(
      () => ({
        collapsed,
        onCollapsedChange,
      }),
      [collapsed, onCollapsedChange]
    );

    const sidebarClasses = [
      // Base styles
      'flex flex-col h-full',
      // Background and border
      'bg-secondary-100 border-r border-secondary-200',
      // Width transition
      'transition-[width] duration-200 ease-out',
      // Width based on collapsed state
      collapsed ? 'w-16' : 'w-60',
      // Overflow handling: the nav is the scroll container (Header/Footer are sticky)
      'overflow-y-auto overflow-x-hidden',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <SidebarContext.Provider value={contextValue}>
        <nav ref={ref} className={sidebarClasses} aria-label="Main navigation" {...props}>
          {children}
        </nav>
      </SidebarContext.Provider>
    );
  }
);

SidebarRoot.displayName = 'Sidebar';

/**
 * SidebarHeader component
 *
 * Displays the logo and optional title at the top of the sidebar.
 * Title is hidden when sidebar is collapsed.
 */
const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ logo, title, className = '', children, ...props }, ref) => {
    const { collapsed } = useSidebarContext();

    const headerClasses = [
      // Height and layout
      'h-16 flex items-center',
      // Padding
      collapsed ? 'justify-center px-3' : 'px-4 gap-3',
      // Border bottom
      'border-b border-secondary-200',
      // Shrink prevention
      'flex-shrink-0',
      // Pinned to the top of the scrolling nav
      'sticky top-0 z-10 bg-secondary-100',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={headerClasses} {...props}>
        {logo && <div className="flex-shrink-0">{logo}</div>}
        {!collapsed && title && (
          <span className="text-lg font-semibold text-secondary-800 truncate">{title}</span>
        )}
        {children}
      </div>
    );
  }
);

SidebarHeader.displayName = 'Sidebar.Header';

/**
 * SidebarSection component
 *
 * Groups navigation items with an optional section label.
 * Label is hidden when sidebar is collapsed.
 */
const SidebarSection = React.forwardRef<HTMLDivElement, SidebarSectionProps>(
  ({ title, children, className = '', ...props }, ref) => {
    const { collapsed } = useSidebarContext();

    const sectionClasses = ['py-2', className].filter(Boolean).join(' ');

    const labelClasses = [
      'px-3 py-2',
      'text-xs font-medium uppercase tracking-wider',
      'text-secondary-500',
      collapsed ? 'sr-only' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={sectionClasses} {...props}>
        {title && (
          <div className={labelClasses} aria-hidden={collapsed}>
            {title}
          </div>
        )}
        <ul  className="space-y-1 px-2">
          {children}
        </ul>
      </div>
    );
  }
);

SidebarSection.displayName = 'Sidebar.Section';

/**
 * Get the state-based CSS classes for a sidebar item
 */
function getItemStateClasses(disabled: boolean, active: boolean): string {
  if (disabled) {
    return 'text-secondary-400 cursor-not-allowed';
  }
  if (active) {
    return 'bg-primary-100 text-primary-800 border-l-[3px] border-primary-600 -ml-0.5 pl-[10px]';
  }
  return 'text-secondary-700 hover:bg-secondary-200';
}

/**
 * Build the complete CSS class string for a sidebar item
 */
function buildItemClasses(
  disabled: boolean,
  active: boolean,
  collapsed: boolean,
  className: string
): string {
  const baseClasses = [
    'flex items-center gap-3 w-full',
    'h-10 px-3 rounded-lg',
    'text-sm font-medium',
    'transition-colors duration-150',
    getItemStateClasses(disabled, active),
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2',
  ];

  if (collapsed) {
    baseClasses.push('justify-center px-0');
  }

  if (className) {
    baseClasses.push(className);
  }

  return baseClasses.join(' ');
}

/**
 * Props for rendering the interactive element
 */
interface InteractiveElementOptions {
  itemRef: React.Ref<HTMLElement>;
  href?: string;
  disabled: boolean;
  active: boolean;
  hasChildren: boolean;
  expanded: boolean;
  itemClasses: string;
  content: React.ReactNode;
  handleClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  props: React.HTMLAttributes<HTMLElement>;
}

/**
 * Create the link element for sidebar item
 */
function createLinkElement(options: InteractiveElementOptions): React.ReactElement {
  const { itemRef, href, disabled, active, itemClasses, content, handleClick, props } = options;
  return (
    <a
      ref={itemRef as React.Ref<HTMLAnchorElement>}
      href={disabled ? undefined : href}
      className={itemClasses}
      aria-current={active ? 'page' : undefined}
      aria-disabled={disabled}
      onClick={handleClick}
      {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
    >
      {content}
    </a>
  );
}

/**
 * Create the button element for sidebar item
 */
function createButtonElement(options: InteractiveElementOptions): React.ReactElement {
  const { itemRef, disabled, hasChildren, expanded, itemClasses, content, handleClick, handleKeyDown, props } = options;
  return (
    <button
      ref={itemRef as React.Ref<HTMLButtonElement>}
      type="button"
      className={itemClasses}
      disabled={disabled}
      aria-expanded={hasChildren ? expanded : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
}

/**
 * Create the interactive element (link or button) for sidebar item
 */
function createInteractiveElement(options: InteractiveElementOptions): React.ReactElement {
  if (options.href) {
    return createLinkElement(options);
  }
  return createButtonElement(options);
}

/**
 * SidebarItem component
 *
 * Individual navigation item. Renders as a link (<a>) if href is provided,
 * otherwise renders as a button. Supports nested items that expand/collapse.
 */
const SidebarItem = React.forwardRef<HTMLElement, SidebarItemProps>(
  (
    {
      icon,
      label,
      href,
      active = false,
      disabled = false,
      children,
      className = '',
      onClick,
      ...props
    },
    ref
  ) => {
    const { collapsed } = useSidebarContext();
    const [expanded, setExpanded] = React.useState(false);
    const hasChildren = React.Children.count(children) > 0;

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      if (hasChildren) {
        e.preventDefault();
        setExpanded(!expanded);
      }
      onClick?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && hasChildren) {
        e.preventDefault();
        setExpanded(!expanded);
      }
    };

    const itemClasses = buildItemClasses(disabled, active, collapsed, className);

    const content = (
      <>
        {icon && (
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">{icon}</span>
        )}
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{label}</span>
            {hasChildren && (
              <span className="flex-shrink-0">
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </span>
            )}
          </>
        )}
      </>
    );

    const interactiveOptions: InteractiveElementOptions = {
      itemRef: ref,
      href,
      disabled,
      active,
      hasChildren,
      expanded,
      itemClasses,
      content,
      handleClick,
      handleKeyDown,
      props,
    };

    const interactiveElement = createInteractiveElement(interactiveOptions);

    const wrappedContent = collapsed ? (
      <Tooltip content={label} position="right">
        {interactiveElement}
      </Tooltip>
    ) : (
      interactiveElement
    );

    const showNestedItems = hasChildren && expanded && !collapsed;

    return (
      <li>
        {wrappedContent}
        {showNestedItems && (
          <ul className="ml-6 mt-1 space-y-1">
            {children}
          </ul>
        )}
      </li>
    );
  }
);

SidebarItem.displayName = 'Sidebar.Item';

/**
 * SidebarFooter component
 *
 * Footer area at the bottom of the sidebar.
 * Typically used for user profile, settings, or collapse toggle.
 */
const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ children, className = '', ...props }, ref) => {
    const footerClasses = [
      // Push to bottom, pinned to the bottom of the scrolling nav
      'mt-auto flex-shrink-0',
      'sticky bottom-0 z-10 bg-secondary-100',
      // Padding
      'py-2 px-2',
      // Border top
      'border-t border-secondary-200',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={footerClasses} {...props}>
        <ul  className="space-y-1">
          {children}
        </ul>
      </div>
    );
  }
);

SidebarFooter.displayName = 'Sidebar.Footer';

/**
 * Sidebar compound component
 *
 * Main navigation sidebar with collapsible states.
 * Use the compound component pattern for flexible layout:
 *
 * - Sidebar.Header - Top area with logo and title
 * - Sidebar.Section - Grouped navigation items with optional label
 * - Sidebar.Item - Individual navigation item
 * - Sidebar.Footer - Bottom area for settings/profile
 *
 * @example
 * <Sidebar collapsed={false}>
 *   <Sidebar.Header logo={<Logo />} title="Insurance Portal" />
 *   <Sidebar.Section title="Main Navigation">
 *     <Sidebar.Item icon={<HomeIcon />} label="Dashboard" href="/" active />
 *     <Sidebar.Item icon={<FileIcon />} label="Policies" href="/policies" />
 *   </Sidebar.Section>
 *   <Sidebar.Footer>
 *     <Sidebar.Item icon={<SettingsIcon />} label="Settings" href="/settings" />
 *   </Sidebar.Footer>
 * </Sidebar>
 */
const Sidebar = Object.assign(SidebarRoot, {
  Header: SidebarHeader,
  Section: SidebarSection,
  Item: SidebarItem,
  Footer: SidebarFooter,
});

export { Sidebar, SidebarHeader, SidebarSection, SidebarItem, SidebarFooter };
